<template>
  <div class="space-y-6">
    <h2 class="text-2xl font-semibold text-gray-800">Network Ports Monitor</h2>
    
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <!-- Common Web Ports -->
      <div class="bg-white p-4 rounded-lg shadow">
        <h3 class="text-lg font-medium mb-3">Web Services</h3>
        <div class="space-y-2">
          <div class="flex justify-between items-center">
            <span>HTTP (80)</span>
            <span class="px-2 py-1 rounded-full text-sm" 
                  :class="portStatus.http ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
              {{ portStatus.http ? 'Active' : 'Inactive' }}
            </span>
          </div>
          <div class="flex justify-between items-center">
            <span>HTTPS (443)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">Active</span>
          </div>
          <div class="flex justify-between items-center">
            <span>WebSocket (8080, 3000)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">Active</span>
          </div>
          <div class="flex justify-between items-center">
            <span>WebSocket SSL (4443)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">Active</span>
          </div>
        </div>
      </div>

      <!-- Database Ports -->
      <div class="bg-white p-4 rounded-lg shadow">
        <h3 class="text-lg font-medium mb-3">Database Services</h3>
        <div class="space-y-2">
          <div class="flex justify-between items-center">
            <span>MySQL (3306)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">Active</span>
          </div>
          <div class="flex justify-between items-center">
            <span>PostgreSQL (5432)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-gray-100 text-gray-800">Inactive</span>
          </div>
          <div class="flex justify-between items-center">
            <span>MongoDB (27017)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-gray-100 text-gray-800">Inactive</span>
          </div>
          <div class="flex justify-between items-center">
            <span>Redis (6379)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">Active</span>
          </div>
          <div class="flex justify-between items-center">
            <span>Elasticsearch (9200, 9300)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-gray-100 text-gray-800">Inactive</span>
          </div>
          <div class="flex justify-between items-center">
            <span>Cassandra (9042)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-gray-100 text-gray-800">Inactive</span>
          </div>
        </div>
      </div>

      <!-- Email & Communication Ports -->
      <div class="bg-white p-4 rounded-lg shadow">
        <h3 class="text-lg font-medium mb-3">Email & Communication</h3>
        <div class="space-y-2">
          <div class="flex justify-between items-center">
            <span>SMTP (25, 587, 465)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">Active</span>
          </div>
          <div class="flex justify-between items-center">
            <span>IMAP (143, 993)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">Active</span>
          </div>
          <div class="flex justify-between items-center">
            <span>POP3 (110, 995)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-gray-100 text-gray-800">Inactive</span>
          </div>
          <div class="flex justify-between items-center">
            <span>DNS (53)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">Active</span>
          </div>
          <div class="flex justify-between items-center">
            <span>LDAP (389, 636)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-gray-100 text-gray-800">Inactive</span>
          </div>
        </div>
      </div>

      <!-- Development & Tools -->
      <div class="bg-white p-4 rounded-lg shadow">
        <h3 class="text-lg font-medium mb-3">Development & Tools</h3>
        <div class="space-y-2">
          <div class="flex justify-between items-center">
            <span>Node.js (3000)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">Active</span>
          </div>
          <div class="flex justify-between items-center">
            <span>React Dev (5173)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">Active</span>
          </div>
          <div class="flex justify-between items-center">
            <span>Vue Dev (8080)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">Active</span>
          </div>
          <div class="flex justify-between items-center">
            <span>GraphQL (4000)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">Active</span>
          </div>
          <div class="flex justify-between items-center">
            <span>Webpack Dev (8888)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-gray-100 text-gray-800">Inactive</span>
          </div>
        </div>
      </div>

      <!-- System & Security -->
      <div class="bg-white p-4 rounded-lg shadow">
        <h3 class="text-lg font-medium mb-3">System & Security</h3>
        <div class="space-y-2">
          <div class="flex justify-between items-center">
            <span>SSH (22)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">Active</span>
          </div>
          <div class="flex justify-between items-center">
            <span>FTP (20, 21)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-gray-100 text-gray-800">Inactive</span>
          </div>
          <div class="flex justify-between items-center">
            <span>SFTP (22)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">Active</span>
          </div>
          <div class="flex justify-between items-center">
            <span>RDP (3389)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-gray-100 text-gray-800">Inactive</span>
          </div>
          <div class="flex justify-between items-center">
            <span>VNC (5900)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-gray-100 text-gray-800">Inactive</span>
          </div>
        </div>
      </div>

      <!-- Container & Orchestration -->
      <div class="bg-white p-4 rounded-lg shadow">
        <h3 class="text-lg font-medium mb-3">Container & Orchestration</h3>
        <div class="space-y-2">
          <div class="flex justify-between items-center">
            <span>Docker (2375, 2376)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">Active</span>
          </div>
          <div class="flex justify-between items-center">
            <span>Kubernetes API (6443)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">Active</span>
          </div>
          <div class="flex justify-between items-center">
            <span>Kubernetes Nodes (10250)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">Active</span>
          </div>
          <div class="flex justify-between items-center">
            <span>etcd (2379, 2380)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">Active</span>
          </div>
          <div class="flex justify-between items-center">
            <span>Prometheus (9090)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">Active</span>
          </div>
          <div class="flex justify-between items-center">
            <span>Grafana (3000)</span>
            <span class="px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">Active</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const portStatus = ref({
  http: true,
  https: true,
  mysql: true,
  // Add more port statuses as needed
})
</script> 